import React, { useEffect, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import { ContestEntry } from "interfaces/leaderboard/contest";
import { CONTEST_ENTRY_STATUS, sparketGold } from "utils/constants";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import { CheckCircleFill, DashCircleDotted } from "react-bootstrap-icons";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import {
  getContestEntryById,
  getCurrentEntryIdByContestId,
  setContestCurrentEntry,
  refreshUserContestEntry,
} from "state/slices/contests-slice.ts";

interface Props {
  contestId: string;
  entries: ContestEntry[];
}

const ContestEntrySelector: React.FC<Props> = ({ contestId, entries }) => {
  const dispatch = useAppDispatch();
  const fetchedEntriesRef = useRef<Set<string>>(new Set());

  const currentContestEntryId = useAppSelector(state => getCurrentEntryIdByContestId(state, contestId));
  const currentContestEntry = useAppSelector(state => getContestEntryById(state, currentContestEntryId));

  // Refresh each entry once so its status reflects any bets placed elsewhere.
  useEffect(() => {
    entries.forEach(entry => {
      if (!fetchedEntriesRef.current.has(entry.id)) {
        dispatch(refreshUserContestEntry(entry.id));
        fetchedEntriesRef.current.add(entry.id);
      }
    });
  }, [entries, dispatch]);

  const sortedByCreatedAtDescEntries = [...entries].sort((a, b) => {
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  const selectedEntryPosition = sortedByCreatedAtDescEntries.findIndex(entry => entry.id === currentContestEntryId);
  const currentEntryIndex = selectedEntryPosition + 1;

  const dropdownWidth = "60px";

  const toggleBgColor = "#333333"; // Dark gray
  const menuBgColor = "#222222"; // Even darker gray
  const activeItemBgColor = "#444444"; // Medium gray
  const textColor = "#FFFFFF"; // White text for contrast
  const activeTextColor = "#FFFFFF"; // Keep white text for active items too

  if (!currentContestEntry) {
    return <div />;
  }

  const renderStatusIcon = (entry: ContestEntry) => (
    entry.status === CONTEST_ENTRY_STATUS.VALID ? (
      <CheckCircleFill size={12} color="var(--sparket-green)" />
    ) : (
      <DashCircleDotted size={12} color={sparketGold} />
    )
  );

  return (
    <Dropdown align="start">
      <Dropdown.Toggle
        style={{
          backgroundColor: toggleBgColor,
          border: "none",
          color: textColor,
          padding: "4px 8px",
          display: "flex",
          alignItems: "center",
          width: dropdownWidth,
          justifyContent: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {renderStatusIcon(currentContestEntry)}
          <span style={{ marginLeft: 4 }}>
            <BlockyHeavyText style={{ fontSize: 11 }}>
              {currentEntryIndex}
            </BlockyHeavyText>
          </span>
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu style={{
        backgroundColor: menuBgColor,
        width: dropdownWidth,
        minWidth: dropdownWidth,
        padding: "2px",
      }}>
        {sortedByCreatedAtDescEntries.map((entry, index) => (
          <Dropdown.Item
            key={entry.id}
            onClick={() => {
              dispatch(setContestCurrentEntry({ contestId: contestId, entryId: entry.id }));
            }}
            active={entry.id === currentContestEntry.id}
            style={{
              backgroundColor: entry.id === currentContestEntry.id ? activeItemBgColor : "transparent",
              color: entry.id === currentContestEntry.id ? activeTextColor : textColor,
              display: "flex",
              alignItems: "center",
              padding: "4px 8px",
              width: "100%",
              justifyContent: "center",
              margin: "2px 0",
              borderRadius: "3px",
            }}
          >
            {renderStatusIcon(entry)}
            <span style={{ marginLeft: 10 }}>
              <BlockyHeavyText style={{ fontSize: 11 }}>
                {index + 1}
              </BlockyHeavyText>
            </span>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ContestEntrySelector;